import pLimit from "p-limit";
import { DateTime } from "luxon";
import type { CourseParticipant } from "../../providers/courseParticipantsProvider";

export interface CourseAutoFormTask {
  kind: "autoForm";
  studentId: string;
  studentName: string;
  bookedQuoteId: string;
  formId: string;
  formName: string;
}

export interface CourseSGBIIIFormTask {
  kind: "sgbIII";
  studentId: string;
  studentName: string;
  formName: string;
  year: number;
  month: number;
}

export type CourseFormTask = CourseAutoFormTask | CourseSGBIIIFormTask;

export const SGB_III_FORM_NAME = "SGB III - FbW Bescheinigung";

export function getCourseMonths(start: DateTime, end: DateTime): Array<DateTime> {
  if (!start.isValid || !end.isValid || end < start) {
    throw new Error("Der Kurs hat keinen gültigen Zeitraum.");
  }
  const months: Array<DateTime> = [];
  const lastMonth = end.startOf("month");
  for (let month = start.startOf("month"); month <= lastMonth; month = month.plus({ months: 1 })) {
    months.push(month);
  }
  return months;
}

export interface GeneratedCourseAutoForm {
  task: CourseFormTask;
  fileName: string;
  pdf: Blob;
}

export interface FailedCourseAutoForm {
  task: CourseFormTask;
  error: unknown;
}

export function prepareCourseFormTasks(options: {
  selectedParticipantIds: Array<string>;
  students: Array<{ id: string; name: string }>;
  participants: Array<CourseParticipant>;
  forms: Array<{ id: string; name: string }>;
  sgbIIIMonth: DateTime | null | undefined;
}): Array<CourseFormTask> {
  const { selectedParticipantIds, students, participants, forms, sgbIIIMonth } = options;
  if (selectedParticipantIds.length === 0 || (forms.length === 0 && sgbIIIMonth === undefined)) {
    throw new Error("Bitte wähle Teilnehmer und mindestens ein Formular aus.");
  }
  if (sgbIIIMonth !== undefined && (!sgbIIIMonth?.isValid || sgbIIIMonth.year < 2000 || sgbIIIMonth.year > 2100)) {
    throw new Error("Bitte wähle einen gültigen Monat und ein Jahr aus.");
  }
  const participantsById = new Map(participants.map((participant) => [participant.studentId, participant]));
  const studentsById = new Map(students.map((student) => [student.id, student]));
  return selectedParticipantIds.flatMap((id) => {
    const participant = participantsById.get(id);
    if (!participant) {
      throw new Error(`Teilnehmer ${id} ist nicht mehr im Kurs. Bitte wähle erneut aus.`);
    }
    const student = studentsById.get(id);
    if (!student || (forms.length > 0 && !participant.bookedQuoteId)) {
      throw new Error(`Teilnehmer ${id} oder zugehöriges gebuchtes Paket fehlen.`);
    }
    const formTasks: Array<CourseFormTask> = forms.map((form) => ({
      kind: "autoForm",
      studentId: id,
      studentName: student.name,
      bookedQuoteId: participant.bookedQuoteId,
      formId: form.id,
      formName: form.name,
    }));
    if (sgbIIIMonth) {
      formTasks.push({
        kind: "sgbIII",
        studentId: id,
        studentName: student.name,
        formName: SGB_III_FORM_NAME,
        year: sgbIIIMonth.year,
        month: sgbIIIMonth.month,
      });
    }
    return formTasks;
  });
}

function safeFileName(value: string): string {
  return value.replace(/[^\p{L}\p{N} ._-]/gu, "_").trim();
}

export async function generateCourseAutoForms(
  tasks: Array<CourseFormTask>,
  fetchPdf: (task: CourseFormTask) => Promise<Blob>,
  onProgress: (completed: number) => void,
): Promise<{ generated: Array<GeneratedCourseAutoForm>; failed: Array<FailedCourseAutoForm> }> {
  const limit = pLimit(3);
  let completed = 0;
  const results = await Promise.all(
    tasks.map((task) =>
      limit(async () => {
        try {
          const pdf = await fetchPdf(task);
          if (pdf.size === 0) {
            throw new Error("Das generierte PDF ist leer.");
          }
          return {
            generated: {
              task,
              pdf,
              fileName:
                task.kind === "sgbIII"
                  ? `${safeFileName(task.studentName)}_SGB_III_Bescheinigung_Fehlzeiten_${task.year}_${String(task.month).padStart(2, "0")}.pdf`
                  : `${safeFileName(task.studentName)}_${safeFileName(task.formName)}.pdf`,
            },
          };
        } catch (error) {
          return { failed: { task, error } };
        } finally {
          onProgress(++completed);
        }
      }),
    ),
  );
  return {
    generated: results.flatMap((result) => (result.generated ? [result.generated] : [])),
    failed: results.flatMap((result) => (result.failed ? [result.failed] : [])),
  };
}

export async function createCourseAutoFormsDownload(
  generated: Array<GeneratedCourseAutoForm>,
  courseName: string,
): Promise<{ blob: Blob; fileName: string }> {
  if (generated.length === 0) {
    throw new Error("Keine PDFs zum Herunterladen vorhanden.");
  }
  if (generated.length === 1) {
    return { blob: generated[0].pdf, fileName: generated[0].fileName };
  }
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();
  const usedFileNames = new Set<string>();
  for (const { fileName, pdf } of generated) {
    let uniqueFileName = fileName;
    let suffix = 2;
    while (usedFileNames.has(uniqueFileName.toLowerCase())) {
      uniqueFileName = `${fileName.slice(0, -4)} (${suffix++}).pdf`;
    }
    zip.file(uniqueFileName, pdf);
    usedFileNames.add(uniqueFileName.toLowerCase());
  }
  return {
    blob: await zip.generateAsync({ type: "blob", compression: "DEFLATE" }),
    fileName: `${safeFileName(courseName)}_${DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss")}_${generated.some(({ task }) => task.kind === "sgbIII") ? "Formulare" : "Auto-Formulare"}.zip`,
  };
}

export function downloadCourseAutoForms(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  window.setTimeout(() => {
    anchor.remove();
    URL.revokeObjectURL(url);
  }, 60_000);
}
